import React, { useState, useCallback } from 'react';
import Header from './components/Header';
import StatsBar from './components/StatsBar';
import ImageUpload from './components/ImageUpload';
import DetectionPreview from './components/DetectionPreview';
import ComplianceScore from './components/ComplianceScore';
import AlertPanel from './components/AlertPanel';
import InspectionHistory from './components/InspectionHistory';
import VideoUpload from './components/VideoUpload';
import VideoResults from './components/VideoResults';
import LiveStream from './components/LiveStream';
import WebcamCapture from './components/WebcamCapture';

function App() {
  const [mode, setMode] = useState('industry');
  const [analysisType, setAnalysisType] = useState('image');
  const [inspectionResult, setInspectionResult] = useState(null);
  const [videoResult, setVideoResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const handleInspectionComplete = useCallback((result) => {
    setInspectionResult(result);
    setIsProcessing(false);
    setHistoryRefreshKey(k => k + 1);
  }, []);

  const handleVideoComplete = useCallback((result) => {
    setVideoResult(result);
    setIsProcessing(false);
    setHistoryRefreshKey(k => k + 1);
  }, []);

  const handleProcessingStart = useCallback(() => {
    setIsProcessing(true);
    setInspectionResult(null);
    setVideoResult(null);
  }, []);

  const handleTypeChange = (type) => {
    setAnalysisType(type);
    setInspectionResult(null);
    setVideoResult(null);
    setIsProcessing(false);
  };

  return (
    <div className="app">
      <Header mode={mode} onModeChange={setMode} />

      <main className="main-content">
        {/* Key Metrics Strip */}
        <StatsBar refreshKey={historyRefreshKey} />

        {/* Analysis Type Tabs */}
        <div className="analysis-tabs">
          <button
            className={`analysis-tab ${analysisType === 'image' ? 'active' : ''}`}
            onClick={() => handleTypeChange('image')}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor">
              <path d="M3 2a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V3a1 1 0 00-1-1H3zm10 12H5l3-6 2.25 4.5 1.5-3L14 14z"/>
            </svg>
            Image Inspection
          </button>
          <button
            className={`analysis-tab ${analysisType === 'video' ? 'active' : ''}`}
            onClick={() => handleTypeChange('video')}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor">
              <path d="M3 3a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm4 3l5 3-5 3V6z"/>
            </svg>
            Video Analytics
          </button>
          <button
            className={`analysis-tab ${analysisType === 'live' ? 'active' : ''}`}
            onClick={() => handleTypeChange('live')}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor">
              <circle cx="9" cy="9" r="3"/>
              <path d="M5.5 5.5a5 5 0 000 7M12.5 5.5a5 5 0 010 7M3.3 3.3a8 8 0 000 11.4M14.7 3.3a8 8 0 010 11.4"/>
            </svg>
            Live Stream
          </button>
          <button
            className={`analysis-tab ${analysisType === 'webcam' ? 'active' : ''}`}
            onClick={() => handleTypeChange('webcam')}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor">
              <rect x="2" y="4" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="9" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="9" cy="9" r="1"/>
            </svg>
            Webcam
          </button>
        </div>

        {/* Upload / Stream Section */}
        <section className="upload-section">
          {analysisType === 'image' ? (
            <ImageUpload
              mode={mode}
              onProcessingStart={handleProcessingStart}
              onInspectionComplete={handleInspectionComplete}
              isProcessing={isProcessing}
            />
          ) : analysisType === 'video' ? (
            <VideoUpload
              mode={mode}
              onProcessingStart={handleProcessingStart}
              onVideoComplete={handleVideoComplete}
              isProcessing={isProcessing}
            />
          ) : analysisType === 'live' ? (
            <LiveStream mode={mode} />
          ) : (
            <WebcamCapture mode={mode} />
          )}
        </section>

        {/* Image Results */}
        {analysisType === 'image' && (inspectionResult || isProcessing) && (
          <section className="results-section">
            <div className="results-grid">
              <div className="results-left">
                <DetectionPreview
                  result={inspectionResult}
                  isProcessing={isProcessing}
                />
              </div>
              <div className="results-right">
                <ComplianceScore
                  result={inspectionResult}
                  isProcessing={isProcessing}
                  mode={mode}
                />
                <AlertPanel
                  alerts={inspectionResult?.alerts || []}
                  isProcessing={isProcessing}
                />
              </div>
            </div>
          </section>
        )}

        {/* Video Results */}
        {analysisType === 'video' && (videoResult || isProcessing) && (
          <section className="results-section">
            <VideoResults
              result={videoResult}
              isProcessing={isProcessing}
            />
          </section>
        )}

        {/* History Section */}
        <section className="history-section">
          <InspectionHistory
            mode={mode}
            refreshKey={historyRefreshKey}
          />
        </section>
      </main>

      <footer className="app-footer">
        <p>AI VISION INSPECTOR v1.0 | Computer Vision for Safety & Compliance | Enterprise Demo System</p>
      </footer>
    </div>
  );
}

export default App;
